// 서버 전송(Admin::UploadsController) 호출 공통 처리.
//
// 이 엔드포인트들은 ActionController::Base 위에 있어 CSRF 토큰이 필요하다
// (api/v1 는 ActionController::API 라 불필요 — 두 규칙이 다르다는 점이 함정).
// 여기서 세 가지를 한 번에 처리한다:
//   ① 토큰 자동 첨부
//   ② 토큰 만료 시 서버가 내려준 새 토큰으로 1회 자동 재시도
//      (페이지를 오래 열어두거나 재로그인하면 반드시 밟는 경로)
//   ③ JSON이 아닌 응답(HTML 에러 페이지·리다이렉트)을 사람이 읽을 수 있는 오류로 변환
//      — 예전엔 그대로 response.json() 을 불러 `Unexpected token '<', "<!DOCTYPE "...`
//        라는 원인 불명 메시지가 사용자에게 노출됐다.

export interface UploadResult {
  success: boolean;
  error?: string;
  filename?: string;
  filepath?: string;
  url?: string;
  size?: number;
  [key: string]: any;
}

export function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
}

function setCsrfToken(token: string): void {
  document.querySelector('meta[name="csrf-token"]')?.setAttribute("content", token);
}

// 응답을 JSON으로 해석. HTML(에러 페이지)이면 상태코드에 맞는 한국어 메시지로 바꾼다.
async function readJson(res: Response): Promise<UploadResult> {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    const reason =
        res.status === 403 ? "권한이 없습니다. 시스템 관리자 계정으로 로그인했는지 확인해주세요."
      : res.status === 401 ? "로그인이 필요합니다. 다시 로그인해주세요."
      : res.status === 413 ? "파일이 너무 큽니다."
      : res.status >= 500  ? "서버 오류가 발생했습니다."
      : "요청을 처리하지 못했습니다.";
    return { success: false, error: `${reason} (HTTP ${res.status})` };
  }
}

// CSRF 토큰을 실어 POST/DELETE 하고 JSON을 돌려준다. 토큰 만료면 1회 재시도.
export async function requestWithCsrf(
  url: string,
  init: RequestInit & { jsonBody?: unknown } = {},
): Promise<UploadResult> {
  const { jsonBody, ...rest } = init;

  const send = (token: string) => fetch(url, {
    ...rest,
    method: rest.method || "POST",
    credentials: "same-origin",
    headers: {
      ...(jsonBody !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(rest.headers || {}),
      "X-CSRF-Token": token,
    },
    ...(jsonBody !== undefined ? { body: JSON.stringify(jsonBody) } : {}),
  });

  let res = await send(csrfToken());
  let data = await readJson(res);

  if (data?.error_code === "invalid_csrf" && data.csrf_token) {
    setCsrfToken(data.csrf_token);          // 다음 요청부터는 새 토큰 사용
    res = await send(data.csrf_token);
    data = await readJson(res);
  }
  return data;
}
